import React, { useState, useEffect } from 'react';
import api from '../api';

const AWSMonitoringTab = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedHOA, setSelectedHOA] = useState(null);
  const [hoaDetails, setHOADetails] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [searchHOA, setSearchHOA] = useState('');
  const [searchType, setSearchType] = useState('id'); // 'id' or 'name'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [credentials, setCredentials] = useState({
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    region: 'us-east-2'
  });

  useEffect(() => {
    fetchOverview();
    fetchCredentialsStatus();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/platform-admin/aws/overview');
      setOverview(data);
    } catch (error) {
      console.error('Error fetching AWS overview:', error);
      alert('Failed to load AWS overview');
    } finally {
      setLoading(false);
    }
  };

  const fetchHOADetails = async (hoaId) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/api/platform-admin/aws/hoa/${hoaId}`);
      setHOADetails(data);
      setSelectedHOA(hoaId);
    } catch (error) {
      console.error('Error fetching HOA details:', error);
      alert('Failed to load HOA details');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    if (!confirm('Sync all HOAs? This will check S3 status for all HOAs.')) return;
    
    try {
      setSyncing(true);
      const { data } = await api.post('/api/platform-admin/aws/sync-now');
      alert(`✅ Sync complete!\n\n${data.message}\n\nCheck results in the table below.`);
      await fetchOverview();
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Failed to sync: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncOne = async (hoaId) => {
    try {
      setSyncing(true);
      const { data } = await api.post(`/api/platform-admin/aws/sync-now?hoa_id=${hoaId}`);
      alert(`✅ Sync complete for this HOA!\n\n${data.results[0]?.sync_status}`);
      await fetchOverview();
      if (selectedHOA === hoaId) {
        await fetchHOADetails(hoaId);
      }
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Failed to sync: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchHOA || !searchQuery) {
      alert('Please select HOA and enter search query');
      return;
    }

    try {
      setSearching(true);
      const params = new URLSearchParams({
        hoa_id: searchHOA,
        [searchType === 'id' ? 'document_id' : 'document_name']: searchQuery
      });
      
      const { data } = await api.get(`/api/platform-admin/aws/search-document?${params}`);
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching:', error);
      alert('Search failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSearching(false);
    }
  };

  const fetchCredentialsStatus = async () => {
    try {
      const { data } = await api.get('/api/platform-admin/aws/credentials-status');
      setCredentialsStatus(data);
    } catch (error) {
      console.error('Error fetching credentials status:', error);
    }
  };

  const handleTriggerSync = async () => {
    if (!confirm('Trigger manual AWS sync? This will sync all documents to S3.')) return;
    
    try {
      setSyncing(true);
      const { data } = await api.post('/api/platform-admin/aws/trigger-sync');
      alert(`✅ Manual sync completed!\n\nSynced: ${data.result?.synced || 0}\nFailed: ${data.result?.failed || 0}`);
      await fetchOverview();
    } catch (error) {
      console.error('Error triggering sync:', error);
      alert('Failed to trigger sync: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateCredentials = async () => {
    if (!credentials.access_key_id || !credentials.secret_access_key || !credentials.bucket_name) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const { data } = await api.post('/api/platform-admin/aws/update-credentials', credentials);
      
      // Build detailed success message
      let message = '✅ AWS Credentials Updated Successfully!\n\n';
      message += data.message + '\n\n';
      
      if (data.credentials_valid) {
        message += '✅ Credentials validated with S3\n';
      } else {
        message += '⚠️ ' + data.test_message + '\n';
      }
      
      if (data.restart_success) {
        message += '✅ Backend service restarted automatically\n';
        message += '✅ New credentials active immediately!';
      } else if (data.restart_attempted) {
        message += '⚠️ Backend restart failed - please restart manually\n';
        message += 'Run: sudo supervisorctl restart backend';
      } else {
        message += '💡 ' + data.note;
      }
      
      alert(message);
      setShowCredentialsModal(false);
      
      // Wait a moment if backend restarted
      if (data.restart_success) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      await fetchCredentialsStatus();
      await fetchOverview();
    } catch (error) {
      console.error('Error updating credentials:', error);
      alert('❌ Failed to update credentials:\n\n' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchSyncLogs = async () => {
    try {
      const { data } = await api.get('/api/platform-admin/aws/sync-logs?limit=20');
      setSyncLogs(data.logs || []);
      setShowLogsModal(true);
    } catch (error) {
      console.error('Error fetching sync logs:', error);
      alert('Failed to fetch sync logs: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Document Recovery/Sync functions
  const [documentSyncStatus, setDocumentSyncStatus] = useState(null);
  const [documentSyncResults, setDocumentSyncResults] = useState(null);
  const [showSyncResultsModal, setShowSyncResultsModal] = useState(false);

  const fetchDocumentSyncStatus = async () => {
    try {
      const { data } = await api.get('/api/documents/sync/status');
      setDocumentSyncStatus(data);
      
      // Show feedback to user
      const message = `📊 Document Sync Status:\n\n` +
        `Documents needing sync: ${data.needs_sync}\n` +
        `- In documents collection: ${data.documents_collection}\n` +
        `- In hoa_documents collection: ${data.hoa_documents_collection}`;
      
      alert(message);
    } catch (error) {
      console.error('Error fetching document sync status:', error);
      alert('❌ Failed to check status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRecoverDocuments = async (hoaId = null) => {
    const message = hoaId 
      ? `Recover/sync documents for this HOA?\n\nThis will find documents with local file paths and migrate them to S3.`
      : `Recover/sync documents for ALL HOAs?\n\nThis will find all documents with local file paths and migrate them to S3.\n\nWARNING: This may take several minutes for large HOAs.`;
    
    if (!confirm(message)) return;
    
    try {
      setSyncing(true);
      const url = hoaId 
        ? `/api/documents/sync/run?hoa_id=${hoaId}`
        : '/api/documents/sync/run';
      
      const { data } = await api.post(url);
      setDocumentSyncResults(data);
      setShowSyncResultsModal(true);
      
      // Refresh overview after sync
      await fetchOverview();
      await fetchDocumentSyncStatus();
      
    } catch (error) {
      console.error('Error syncing documents:', error);
      alert('❌ Document sync failed:\n\n' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (overview) {
      fetchDocumentSyncStatus();
    }
  }, [overview]);

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading AWS storage data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">AWS Storage Monitoring</h2>
          <button
            onClick={fetchOverview}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            🔄 Refresh
          </button>
        </div>

        {overview && (
          <>
            {/* Storage Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-blue-600 font-medium">Active Storage</div>
                <div className="text-2xl font-bold text-blue-900 mt-1">
                  {overview.overview.active_storage_display || overview.overview.total_storage_display}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  of {overview.overview.storage_limit_gb} GB (Billed)
                </div>
              </div>
              
              <div className="bg-cyan-50 p-4 rounded-lg">
                <div className="text-sm text-cyan-600 font-medium">S3 Total</div>
                <div className="text-2xl font-bold text-cyan-900 mt-1">
                  {overview.overview.s3_storage_display || '0 MB'}
                </div>
                <div className="text-xs text-cyan-600 mt-1">
                  Includes archives
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-green-600 font-medium">Total Files</div>
                <div className="text-2xl font-bold text-green-900 mt-1">
                  {overview.overview.total_files_in_s3}
                </div>
                <div className="text-xs text-green-600 mt-1">in S3</div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-purple-600 font-medium">Total HOAs</div>
                <div className="text-2xl font-bold text-purple-900 mt-1">
                  {overview.overview.total_hoas}
                </div>
                <div className="text-xs text-purple-600 mt-1">monitored</div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-sm text-orange-600 font-medium">Storage Used</div>
                <div className="text-2xl font-bold text-orange-900 mt-1">
                  {overview.overview.storage_percentage}%
                </div>
                <div className="text-xs text-orange-600 mt-1">capacity</div>
              </div>
            </div>

            {/* Storage Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Active Storage (Counts toward plan)</span>
                <span className="text-sm text-gray-600">
                  {overview.overview.active_storage_display || overview.overview.total_storage_display} / {overview.overview.storage_limit_gb} GB
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    overview.overview.storage_percentage > 80
                      ? 'bg-red-500'
                      : overview.overview.storage_percentage > 60
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(overview.overview.storage_percentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Credentials Status Alert */}
            {credentialsStatus && !credentialsStatus.s3_working && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">AWS Connection Issue</h3>
                    <p className="mt-1 text-sm text-red-700">
                      {credentialsStatus.error || 'Unable to connect to AWS S3'}
                    </p>
                    <button
                      onClick={() => setShowCredentialsModal(true)}
                      className="mt-2 text-sm font-medium text-red-800 hover:text-red-900"
                    >
                      Configure AWS Credentials →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sync Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div className="md:col-span-2">
                <div className="text-sm font-medium text-gray-900">Manual Sync Operations</div>
                <div className="text-xs text-gray-600 mt-1">
                  Trigger background job to sync all documents to S3
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleTriggerSync}
                  disabled={syncing}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {syncing ? '🔄 Syncing...' : '🚀 Trigger Full Sync'}
                </button>
                <button
                  onClick={fetchSyncLogs}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  📋 Logs
                </button>
              </div>
            </div>

            {/* Document Recovery Section */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 bg-red-50 border-2 border-red-200 p-4 rounded-lg">
              <div className="md:col-span-2">
                <div className="text-sm font-bold text-red-900">🚨 Document Recovery & Migration</div>
                <div className="text-xs text-gray-700 mt-2 space-y-1">
                  <p>Find and recover documents uploaded before S3 migration.</p>
                  <p className="font-medium">Use this if documents show 500/404 errors when viewing.</p>
                  {documentSyncStatus && (
                    <div className="mt-2 bg-white rounded p-2">
                      <p className="font-semibold text-gray-900">
                        📊 Documents needing sync: {documentSyncStatus.needs_sync}
                      </p>
                      {documentSyncStatus.last_sync && (
                        <p className="text-xs text-gray-600 mt-1">
                          Last sync: {new Date(documentSyncStatus.last_sync).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleRecoverDocuments()}
                  disabled={syncing}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {syncing ? '🔄 Recovering...' : '🔧 Recover All Documents'}
                </button>
                <button
                  onClick={fetchDocumentSyncStatus}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  📊 Check Status
                </button>
              </div>
            </div>

            {/* Admin Controls */}
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => setShowCredentialsModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                🔑 Manage AWS Credentials
              </button>
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
              >
                🔄 Check Sync Status
              </button>
            </div>

            {/* AWS Info */}
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Bucket:</span>
                <span className="ml-2 font-mono text-gray-900">{overview.overview.bucket_name}</span>
              </div>
              <div>
                <span className="text-gray-600">Region:</span>
                <span className="ml-2 font-mono text-gray-900">{overview.overview.region}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* HOA Breakdown Table */}
      {overview && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Storage by HOA</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HOA Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Files in S3</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Files in DB</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {overview.hoa_breakdown.map((hoa) => (
                  <tr key={hoa.hoa_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{hoa.hoa_name}</div>
                      <div className="text-xs text-gray-500">{hoa.hoa_id.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-semibold">{hoa.active_storage_display || hoa.storage_display}</div>
                      <div className="text-xs text-gray-500">
                        of {hoa.storage_limit_gb || 25}GB ({hoa.storage_percentage || 0}%)
                      </div>
                      {hoa.s3_storage_display && hoa.s3_storage_display !== '0 MB' && (
                        <div className="text-xs text-blue-600 mt-1">
                          S3: {hoa.s3_storage_display}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {hoa.files_in_s3}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {hoa.files_in_db}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {hoa.files_in_s3 > 0 && hoa.files_in_s3 === hoa.files_in_db ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          ✅ Synced
                        </span>
                      ) : hoa.files_in_s3 === 0 && hoa.files_in_db > 0 ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          ⚠️ Needs Sync
                        </span>
                      ) : hoa.files_in_s3 === 0 && hoa.files_in_db === 0 ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          ○ Empty
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          ⚠️ Mismatch
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                      <button
                        onClick={() => fetchHOADetails(hoa.hoa_id)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleSyncOne(hoa.hoa_id)}
                        disabled={syncing}
                        className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                      >
                        Sync
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Document Search */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Search Document in AWS</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select HOA</label>
            <select
              value={searchHOA}
              onChange={(e) => setSearchHOA(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Select HOA --</option>
              {overview?.hoa_breakdown.map((hoa) => (
                <option key={hoa.hoa_id} value={hoa.hoa_id}>
                  {hoa.hoa_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search By</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="id"
                  checked={searchType === 'id'}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Document ID</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="name"
                  checked={searchType === 'name'}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm">Document Name</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchType === 'id' ? 'Enter document ID...' : 'Enter document name...'}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchHOA || !searchQuery}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? '🔍 Searching...' : '🔍 Search'}
          </button>
        </div>

        {/* Search Results */}
        {searchResults && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Search Results ({searchResults.count} found)
            </h4>
            
            {searchResults.results.map((result, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h5 className="font-semibold text-gray-900">{result.title}</h5>
                      {result.in_s3 ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          ✅ In S3
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                          ❌ Not in S3
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Document ID:</span> 
                        <span className="ml-2 font-mono text-xs">{result.document_id}</span>
                      </div>
                      {result.category && (
                        <div>
                          <span className="font-medium">Category:</span> 
                          <span className="ml-2">{result.category}</span>
                        </div>
                      )}
                      {result.s3_details && (
                        <>
                          <div>
                            <span className="font-medium">Size:</span> 
                            <span className="ml-2">{(result.s3_details.size / 1024 / 1024).toFixed(2)} MB</span>
                          </div>
                          <div>
                            <span className="font-medium">Last Modified:</span> 
                            <span className="ml-2">
                              {new Date(result.s3_details.last_modified).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">S3 Key:</span> 
                            <span className="ml-2 font-mono text-xs">{result.s3_details.s3_key}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {result.s3_details && (
                    <a
                      href={result.s3_details.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      📥 Download from S3
                    </a>
                  )}
                </div>

                {result.error && (
                  <div className="mt-2 text-sm text-red-600">
                    Error: {result.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HOA Details Modal */}
      {hoaDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900">{hoaDetails.hoa_name} - AWS Details</h3>
              <button
                onClick={() => {
                  setHOADetails(null);
                  setSelectedHOA(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Storage Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-blue-600 font-medium">Storage</div>
                  <div className="text-xl font-bold text-blue-900 mt-1">
                    {hoaDetails.storage.total_display}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-green-600 font-medium">Files in S3</div>
                  <div className="text-xl font-bold text-green-900 mt-1">
                    {hoaDetails.storage.files_in_s3}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-purple-600 font-medium">Files in DB</div>
                  <div className="text-xl font-bold text-purple-900 mt-1">
                    {hoaDetails.storage.files_in_db}
                  </div>
                </div>
              </div>

              {/* S3 Files List */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Files in S3 (Recent 50)</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {hoaDetails.s3_files.length > 0 ? (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2">File</th>
                          <th className="text-right py-2">Size</th>
                          <th className="text-right py-2">Last Modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hoaDetails.s3_files.map((file, index) => (
                          <tr key={index} className="border-b border-gray-100">
                            <td className="py-2 font-mono text-xs">{file.key.split('/').pop()}</td>
                            <td className="text-right py-2">{(file.size / 1024).toFixed(1)} KB</td>
                            <td className="text-right py-2">{new Date(file.last_modified).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No files in S3</p>
                  )}
                </div>
              </div>

              {/* Sync Status */}
              <div className="bg-gray-100 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">Sync Status</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Last checked: {new Date(hoaDetails.sync_status.last_checked).toLocaleString()}
                    </div>
                  </div>
                  {hoaDetails.sync_status.files_match ? (
                    <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                      ✅ In Sync
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
                      ⚠️ Needs Sync
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AWS Credentials Modal */}
      {showCredentialsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">🔑 AWS Credentials Management</h3>
              <button
                onClick={() => setShowCredentialsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {credentialsStatus && (
                <div className={`p-4 rounded-lg ${credentialsStatus.s3_working ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="text-sm font-medium">
                    {credentialsStatus.s3_working ? '✅ AWS Connected' : '⚠️ AWS Not Connected'}
                  </div>
                  {credentialsStatus.error && (
                    <div className="text-xs text-red-600 mt-1">{credentialsStatus.error}</div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AWS Access Key ID</label>
                <input
                  type="text"
                  value={credentials.access_key_id}
                  onChange={(e) => setCredentials({...credentials, access_key_id: e.target.value})}
                  placeholder="AKIA..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AWS Secret Access Key</label>
                <input
                  type="password"
                  value={credentials.secret_access_key}
                  onChange={(e) => setCredentials({...credentials, secret_access_key: e.target.value})}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">S3 Bucket Name</label>
                <input
                  type="text"
                  value={credentials.bucket_name}
                  onChange={(e) => setCredentials({...credentials, bucket_name: e.target.value})}
                  placeholder="my-bucket-name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AWS Region</label>
                <select
                  value={credentials.region}
                  onChange={(e) => setCredentials({...credentials, region: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-east-2">US East (Ohio)</option>
                  <option value="us-west-1">US West (N. California)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                </select>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                <strong>⚠️ Important:</strong> Updating credentials will modify the .env file. A backend restart is recommended for changes to take full effect.
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleUpdateCredentials}
                  className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  💾 Save Credentials
                </button>
                <button
                  onClick={() => setShowCredentialsModal(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Logs Modal */}
      {showLogsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">📋 AWS Sync Logs</h3>
              <button
                onClick={() => setShowLogsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {syncLogs.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No sync logs found. Sync operations will appear here.
                </div>
              ) : (
                <div className="space-y-4">
                  {syncLogs.map((log, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${log.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-lg ${log.success ? 'text-green-600' : 'text-red-600'}`}>
                              {log.success ? '✅' : '❌'}
                            </span>
                            <span className="font-semibold text-gray-900">
                              {log.success ? 'Sync Completed' : 'Sync Failed'}
                            </span>
                          </div>
                          
                          {log.synced !== undefined && (
                            <div className="mt-2 text-sm text-gray-700">
                              <div>✅ Synced: {log.synced}</div>
                              <div>❌ Failed: {log.failed}</div>
                              <div>📊 Total: {log.total}</div>
                            </div>
                          )}
                          
                          {log.message && (
                            <div className="mt-2 text-sm text-gray-600">{log.message}</div>
                          )}
                          
                          {log.error && (
                            <div className="mt-2 text-sm text-red-600">Error: {log.error}</div>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowLogsModal(false)}
                className="w-full px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Sync Results Modal */}
      {showSyncResultsModal && documentSyncResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">📋 Document Recovery Results</h3>
              <button
                onClick={() => setShowSyncResultsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-900">{documentSyncResults.total_found}</div>
                  <div className="text-xs text-blue-700">Total Found</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-900">{documentSyncResults.synced}</div>
                  <div className="text-xs text-green-700">✅ Synced</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-900">{documentSyncResults.failed}</div>
                  <div className="text-xs text-red-700">❌ Failed</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-900">{documentSyncResults.missing_files}</div>
                  <div className="text-xs text-yellow-700">⚠️ Missing</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{documentSyncResults.already_synced}</div>
                  <div className="text-xs text-gray-700">Already OK</div>
                </div>
              </div>

              {/* Success Message */}
              {documentSyncResults.synced > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="font-semibold text-green-900">
                    ✅ Successfully recovered {documentSyncResults.synced} document(s) to S3!
                  </div>
                  <div className="text-sm text-green-700 mt-1">
                    These documents should now be accessible when viewing.
                  </div>
                </div>
              )}

              {/* Warnings */}
              {documentSyncResults.missing_files > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="font-semibold text-yellow-900">
                    ⚠️ {documentSyncResults.missing_files} document(s) have database records but files don't exist locally
                  </div>
                  <div className="text-sm text-yellow-700 mt-1">
                    These files were never uploaded or were deleted. Recovery is not possible.
                  </div>
                </div>
              )}

              {/* Details */}
              {documentSyncResults.details && documentSyncResults.details.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Detailed Results:</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {documentSyncResults.details.map((detail, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded border text-sm ${
                          detail.status === 'success'
                            ? 'bg-green-50 border-green-200'
                            : detail.status === 'file_not_found' || detail.status === 'missing_path'
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="font-medium">
                          {detail.status === 'success' && '✅ '}
                          {detail.status === 'error' && '❌ '}
                          {(detail.status === 'file_not_found' || detail.status === 'missing_path') && '⚠️ '}
                          {detail.title}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">ID: {detail.doc_id}</div>
                        {detail.message && (
                          <div className="text-xs text-gray-700 mt-1">{detail.message}</div>
                        )}
                        {detail.s3_key && (
                          <div className="text-xs font-mono text-gray-600 mt-1">S3: {detail.s3_key}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowSyncResultsModal(false)}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AWSMonitoringTab;
